define('StellarGrade.ST_RehydrationFix.Main', [
    'Backbone',
    'underscore'
], function (
    Backbone,
    _
) {
    'use strict';

    var CONFIGURATION_KEY = 'stRehydrationFix';
    var MODE_ALL_PRODUCTS = 'All products';
    var SEO_SUPPORT_BODY_CLASS = 'seo-support';
    var PDP_VIEW_ELEMENT_ID = 'ProductDetails.Full.View';
    var IMAGE_GALLERY_PLACEHOLDER = 'Product.ImageGallery';

    var g_isFirstViewHandled = false;
    var g_pdpComponent = null;
    var g_isAllProductsMode = false;
    var g_selectedItemIds = [];

    /**
     * Reads the extension configuration: the mode and the list of selected item IDs (normalized to upper case).
     *
     * @governance 0 units (client-side)
     * @param {Object} container Extensibility API application container.
     * @returns {void}
     */
    function loadConfiguration(container) {
        var environmentComponent = container.getComponent('Environment');
        var fixConfiguration = (environmentComponent && environmentComponent.getConfig(CONFIGURATION_KEY)) || {};

        g_isAllProductsMode = fixConfiguration.mode == MODE_ALL_PRODUCTS;
        // NetSuite saves the list as plain strings; the object form ({ itemId }) is still accepted for safety.
        g_selectedItemIds = _.compact(_.map(fixConfiguration.itemIds || [], function (selectedItem) {
            var selectedItemId = _.isObject(selectedItem) ? selectedItem.itemId : selectedItem;

            return selectedItemId ? String(selectedItemId).trim().toUpperCase() : null;
        }));
    }

    /**
     * Tells whether the product currently shown should get the fix, based on the configured mode.
     *
     * @governance 0 units (client-side)
     * @returns {boolean} True in "All products" mode, or when the item ID is in the selected items.
     */
    function isItemEnabledForFix() {
        var itemInfo = g_pdpComponent.getItemInfo();
        var itemId = itemInfo && itemInfo.item && itemInfo.item.itemid;

        if (g_isAllProductsMode) {
            return true;
        }

        return !!itemId && _.contains(g_selectedItemIds, String(itemId).toUpperCase());
    }

    /**
     * Once rehydration is over, re-renders the PDP so every extension child view is attached, then
     * restores what the re-render does not: the CPQ Configure button visibility and the image gallery
     * slider. If the restore fails, falls back to re-running the current route, which renders the page
     * again the regular (non-rehydrated) way.
     *
     * @governance 0 units (client-side)
     * @param {Object} layout Internal application layout (SC.Application.layout).
     * @returns {void}
     */
    function applyFixWhenRehydrationEnds(layout) {
        if (SC.isRehydrating) {
            layout.once('afterAppendToDom', function onAfterAppendToDom() {
                _.defer(applyFixWhenRehydrationEnds, layout);
            });
            return;
        }

        var productDetailsView = layout.currentView;

        if (!productDetailsView || !productDetailsView.el || productDetailsView.el.id != PDP_VIEW_ELEMENT_ID) {
            return;
        }

        if (!isItemEnabledForFix()) {
            return;
        }

        try {
            var cpqGlobal = window.vcpq;
            var cpqMainView = cpqGlobal && cpqGlobal.main_view;

            // During rehydration the CPQ view is bound to markup that never existed, so it has no element.
            // CPQ reuses this same instance on every render, so it must get a fresh element first.
            if (cpqMainView && cpqMainView.$el && cpqMainView.$el.length == 0) {
                cpqMainView.setElement(document.createElement('div'));
            }

            productDetailsView.render();

            if (cpqGlobal && typeof cpqGlobal.applyItemView == 'function') {
                cpqGlobal.applyItemView();
            }

            // The gallery only builds its slider on the layout "afterAppendView" event, which does not fire on render().
            var galleryInstances = productDetailsView.childViewInstances && productDetailsView.childViewInstances[IMAGE_GALLERY_PLACEHOLDER];
            var galleryEntry = galleryInstances && galleryInstances[IMAGE_GALLERY_PLACEHOLDER];
            var imageGalleryView = galleryEntry && galleryEntry.childViewInstance;

            if (imageGalleryView && typeof imageGalleryView.initSliderZoom == 'function') {
                imageGalleryView.initSliderZoom(true);
            }
        } catch (restoreError) {
            console.log('[ST_RehydrationFix] PDP restore failed, re-running the route instead.', restoreError);

            try {
                Backbone.history.loadUrl(Backbone.history.fragment);
            } catch (reloadRouteError) {
                // Nothing else can be done client-side: the page stays as rehydrated (same state as without this fix).
                console.log('[ST_RehydrationFix] Route re-run failed, page left as rehydrated.', reloadRouteError);
            }
        }
    }

    /**
     * Handles only the first "afterShowContent" of the page load, the only view that is rehydrated.
     *
     * @governance 0 units (client-side)
     * @returns {void}
     */
    function onFirstShowContent() {
        var layout = SC.Application && SC.Application.layout;

        if (g_isFirstViewHandled) {
            return;
        }

        g_isFirstViewHandled = true;

        if (!layout) {
            return;
        }

        // Defer so CPQ's own PDP "afterShowContent" handler has run and registered the current item.
        _.defer(applyFixWhenRehydrationEnds, layout);
    }

    return {
        /**
         * Registers the rehydration fix on the shopping application.
         *
         * @governance 0 units (client-side)
         * @param {Object} container Extensibility API application container.
         * @returns {void}
         */
        mountToApp: function mountToApp(container) {
            var layoutComponent = container.getComponent('Layout');
            var bodyClasses = (document.body.className || '').split(/\s+/);

            g_pdpComponent = container.getComponent('PDP');

            if (!layoutComponent || !g_pdpComponent) {
                return;
            }

            // Must be read at startup: the body class stays after navigation, but only the first view is rehydrated.
            if (SC.isPageGenerator() || !_.contains(bodyClasses, SEO_SUPPORT_BODY_CLASS)) {
                return;
            }

            loadConfiguration(container);

            if (!g_isAllProductsMode && !g_selectedItemIds.length) {
                return;
            }

            layoutComponent.on('afterShowContent', onFirstShowContent);
        }
    };
});
